import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { BoloPanelForm, BoloPanelFormType, BoloPanelView, BoloResource } from '@mark43/cad-api';
import { useCallback } from 'react';
import { filterInvalidBoloFormEntities } from '../form/filter-invalid-bolo-panel-entities';

interface BoloApiProps {
    onSaveBolo?: (boloPanelView: BoloPanelView) => void;
}

export const useBoloApi = (props: BoloApiProps) => {
    const apiClient = useApiClient();

    const saveBoloMutation = useMutation(
        (boloPanelForm: BoloPanelFormType) => {
            return apiClient.request<BoloResource.SaveBoloPanel>({
                method: 'PUT',
                url: 'bolo/panel/',
                data: { boloPanelForm: boloPanelForm as BoloPanelForm },
                timeout: 10000,
            });
        },
        {
            onSuccess(data) {
                const boloPanelView = data.data.data?.boloPanelView;
                if (boloPanelView) {
                    props.onSaveBolo?.(boloPanelView);
                }
            },
        }
    );

    const onSubmit = useCallback(
        (formValues: BoloPanelFormType) => {
            if (saveBoloMutation.isLoading) {
                return;
            }
            return saveBoloMutation.mutateAsync({
                ...filterInvalidBoloFormEntities(formValues),
            });
        },
        [saveBoloMutation]
    );

    const expireBolo = useMutation((boloId: number) => {
        return apiClient.request<BoloResource.ExpireBolo>({
            method: 'POST',
            data: {
                boloId,
            },
            url: 'bolo/expire/',
        });
    });

    const reactivateBolo = useMutation((boloId: number) => {
        return apiClient.request<BoloResource.ReactivateBolo>({
            method: 'POST',
            data: {
                boloId,
            },
            url: 'bolo/reactivate/',
        });
    });

    return {
        saveBoloMutation,
        onSubmit,
        expireBolo,
        reactivateBolo,
    };
};
